import { batch, type Component, createContext, createEffect, createSignal, type JSX, onCleanup, onMount, splitProps, useContext } from 'solid-js'
import type { ZodError, ZodType } from 'zod'
import type { FormElementType, FormFieldRegistrationType, FormPropsType, FormRenderPropsType } from '~/types/Form'
import type { FormValuesType } from '~/types/useForm'
import { collectIssues, visibleErrors } from '~/utils/formErrors'

type ValidationModes = Set<'submit' | 'change' | 'keyup'>

// ─── Context ────────────────────────────────────────────────────────────────
export interface FormContextValue {
  /**
   * Campos que no se leen ni se escriben por el DOM (Select, TagInput,
   * FileInput…). El registro es la fuente de verdad de su valor y la única
   * manera de que `setValue`, `reset` y los valores iniciales que llegan tarde
   * lleguen hasta ellos.
   */
  registerField: (name: string, api: FormFieldRegistrationType) => void
  unregisterField: (name: string) => void
  /** Fuerza a revalidar y republicar los valores (para campos que no disparan
   * eventos DOM al cambiar, como el FileInput). */
  revalidate: () => void
  /** Marca un campo como "tocado" para que su error de Zod se muestre. */
  touchField: (name: string) => void
  /** Error visible de un campo. Con controller delega en él (única fuente,
   * incluye errores manuales); sin controller replica su visibilidad: sólo se
   * muestra si el campo fue tocado o hubo un intento de envío. */
  error: (name: string) => string | undefined
  /** Mapa completo de errores visibles, indexado por el `name` del campo. */
  errors: () => Record<string, string>
  initialValues: Record<string, unknown>
}

export const FormContext = createContext<FormContextValue | null>(null)

export function useFormContext(): FormContextValue | null {
  return useContext(FormContext)
}
// ─────────────────────────────────────────────────────────────────────────────

const Form: Component<FormPropsType> = props => {
  const [local, others] = splitProps(props, ['onSubmit', 'validator', 'validateOn', 'validateDebounce', 'initialValues', 'onValuesChange', 'valuesChangeDebounce', 'controller', 'children'])

  let formRef: HTMLFormElement | null = null
  const registeredFields = new Map<string, FormFieldRegistrationType>()
  const [formValid, setFormValid] = createSignal(true)
  const [formValues, setFormValues] = createSignal<Record<string, unknown>>({})

  // Estado propio del formulario para el modo sin controller: así los campos
  // pueden leer su error del contexto igual que con `useForm`. Con controller,
  // `error()` delega en él y éstas no mandan.
  const [formIssues, setFormIssues] = createSignal<Record<string, string>>({})
  const [formTouched, setFormTouched] = createSignal<string[]>([])
  const [formSubmitted, setFormSubmitted] = createSignal(false)

  const markTouched = (name: string) => {
    setFormTouched(prev => (prev.includes(name) ? prev : [...prev, name]))
  }

  const contextErrors = (): Record<string, string> => {
    if (local.controller) return local.controller.errors()
    return visibleErrors(formIssues(), formTouched(), formSubmitted())
  }

  const contextError = (name: string): string | undefined => {
    if (local.controller) return local.controller.error(name)
    return contextErrors()[name]
  }

  // El controlador aporta esquema, valores iniciales y momento de validación;
  // lo que venga como prop suelta manda sobre él.
  const getValidator = (): ZodType<unknown> | undefined => local.validator ?? (local.controller?.options.schema as ZodType<unknown> | undefined)

  const getInitialValues = (): Record<string, unknown> | undefined => {
    if (local.initialValues) return local.initialValues
    const fromController = local.controller?.options.initialValues
    return typeof fromController === 'function' ? fromController() : fromController
  }

  const getValidationModes = (): ValidationModes => {
    // Con controlador el defecto es 'change': los errores se pintan mientras se
    // escribe porque hay quien los muestre, cosa que sin controlador no ocurre.
    const mode = local.validateOn ?? local.controller?.options.validateOn ?? (local.controller ? 'change' : 'submit')
    return mode === 'all' || mode === 'change+keyup' ? new Set(['submit', 'change', 'keyup'] as const) : new Set([mode] as const)
  }

  const publish = (values: Record<string, unknown>, issues: Record<string, string>, isValid: boolean) => {
    // Las tres señales cuentan la misma verdad: escritas sueltas, quien las lee
    // a medias ve un formulario válido con errores dentro.
    batch(() => {
      setFormValues(values)
      setFormIssues(issues)
      setFormValid(isValid)
      local.controller?.__publish({ values, issues, valid: isValid })
    })
  }

  const isFormElement = (el: Element): el is FormElementType => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement

  const clearFieldError = (el: FormElementType) => {
    el.removeAttribute('data-error')
    el.removeAttribute('aria-invalid')
  }

  const setFieldError = (el: FormElementType, message: string) => {
    el.setAttribute('aria-invalid', 'true')
    el.setAttribute('data-error', message || 'Campo inválido')
  }

  /**
   * Del DOM salen cadenas, y quien las convierte es el esquema (`z.coerce`).
   *
   * La excepción son los campos que **son** de otro tipo y sólo se reflejan en
   * el DOM como texto: un Switch guarda un booleano y un Select múltiple una
   * lista, y a los dos les toca volver a su tipo antes de validar.
   *
   * La conversión va marcada en el propio elemento (`data-boolean`, `data-json`)
   * y no se aplica a ciegas: antes cualquier texto valía, así que escribir
   * `true` o `[hola]` en un campo de texto lo convertía en otra cosa.
   */
  const normalizeFormValue = (value: FormDataEntryValue | null, element: FormElementType): unknown => {
    if (value === null) return null
    if (typeof value !== 'string') return value

    if (element.hasAttribute('data-boolean')) {
      if (value === 'true') return true
      if (value === 'false') return false
      return value
    }

    if (!element.hasAttribute('data-json')) return value

    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }

  const getElementValue = (element: FormElementType, formData: FormData): unknown => {
    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') return element.checked
      if (element.type === 'radio') return element.checked ? element.value : undefined
    }
    if (element instanceof HTMLSelectElement && element.multiple) {
      return Array.from(element.selectedOptions, opt => opt.value)
    }
    return normalizeFormValue(formData.get(element.name), element)
  }

  const collectFormValues = (form: HTMLFormElement): Record<string, unknown> => {
    const formData = new FormData(form)
    const values: Record<string, unknown> = {}

    for (const el of form.elements) {
      if (!isFormElement(el)) continue
      const element = el as FormElementType
      if (!element.name) continue

      const value = getElementValue(element, formData)
      if (value !== undefined) values[element.name] = value
    }

    // Los campos registrados mandan sobre su reflejo en el DOM: ahí el valor es
    // el de verdad (una lista, unos archivos), no una cadena.
    for (const [name, field] of registeredFields) {
      values[name] = field.getValue()
    }

    // Los que ya no tienen ni elemento ni registro —una pestaña que se cambió,
    // un bloque dentro de un `<Show>`— los recuerda el controlador.
    const extra = local.controller?.__extra()
    return extra ? { ...extra, ...values } : values
  }

  /** Recoge, valida y publica. Devuelve los mensajes indexados por campo. */
  const runValidation = (): Record<string, string> => {
    if (!formRef) return {}

    const validator = getValidator()
    const values = collectFormValues(formRef)

    if (!validator) {
      publish(values, {}, true)
      return {}
    }

    const result = validator.safeParse(values)
    const issues = result.success ? {} : collectIssues(result.error as ZodError)
    publish(values, issues, result.success)
    return issues
  }

  /** Refleja los mensajes en el DOM, para lectores de pantalla y para los estilos. */
  const syncFieldErrors = (issues: Record<string, string>, only?: FormElementType) => {
    if (!formRef) return

    for (const el of formRef.elements) {
      if (!isFormElement(el)) continue
      const element = el as FormElementType
      if (only && element !== only) continue
      if (!element.name) continue

      const message = issues[element.name]
      if (message) setFieldError(element, message)
      else clearFieldError(element)
    }
  }

  /**
   * Escribe valores en los campos. Los registrados se escriben por su API: son
   * los que guardan el valor en una señal, y pintarles `element.value` no sirve
   * de nada porque el siguiente render lo pisa.
   */
  const applyValues = (values: Record<string, unknown>) => {
    for (const [name, field] of registeredFields) {
      if (name in values) field.setValue(values[name])
    }

    if (!formRef) return

    for (const el of formRef.elements) {
      if (!isFormElement(el)) continue
      const element = el as FormElementType
      if (!element.name || !(element.name in values)) continue
      if (registeredFields.has(element.name)) continue

      const value = values[element.name]

      if (element instanceof HTMLInputElement) {
        if (element.type === 'checkbox') {
          element.checked = Boolean(value)
        } else if (element.type === 'radio') {
          element.checked = element.value === value
        } else {
          element.value = String(value ?? '')
        }
      } else if (element instanceof HTMLSelectElement && element.multiple) {
        const selectedValues = Array.isArray(value) ? value : [value]
        for (const option of element.options) {
          option.selected = selectedValues.includes(option.value)
        }
      } else {
        element.value = String(value ?? '')
      }
    }
  }

  // ── Context value expuesto a los hijos ───────────────────────────────────
  const contextValue: FormContextValue = {
    registerField(name, api) {
      registeredFields.set(name, api)

      const initial = getInitialValues()?.[name]
      if (initial !== undefined) api.setValue(initial)

      runValidation()
    },
    unregisterField(name) {
      registeredFields.delete(name)
    },
    revalidate() {
      runValidation()
    },
    touchField(name) {
      markTouched(name)
      local.controller?.__touch(name)
    },
    error: contextError,
    errors: contextErrors,
    get initialValues() {
      return getInitialValues() ?? {}
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * `FormData` completo. Sólo se añaden a mano los campos registrados que no
   * tienen elemento en el formulario: los que sí lo tienen ya viajan con el
   * formato que el propio componente eligió para su reflejo, y serializarlos
   * otra vez aquí se lo cambiaría.
   */
  const buildFormData = (form: HTMLFormElement): FormData => {
    const fd = new FormData(form)
    for (const [name, field] of registeredFields) {
      if (form.elements.namedItem(name)) continue
      const value = field.getValue()
      fd.set(name, typeof value === 'string' ? value : JSON.stringify(value))
    }
    return fd
  }

  /**
   * Un solo oyente para todo lo que pasa al escribir.
   *
   * Antes había dos —uno revalidaba sin retardo y el otro validaba con 150 ms de
   * espera—, así que cada tecla costaba dos recorridos del formulario y dos
   * parseos del esquema. La validación silenciosa hace falta siempre: es lo que
   * mantiene al día `valid()`, del que cuelga el botón de guardar. `validateOn`
   * sólo decide si además se marca el campo en el DOM mientras se escribe.
   */
  const setupInputListener = (modes: ValidationModes) => {
    if (!formRef) return

    const liveMarking = modes.has('change') || modes.has('keyup')
    const markDebounce = local.validateDebounce ?? 150
    const changeDebounce = local.valuesChangeDebounce ?? 300
    const markTimers = new Map<EventTarget, ReturnType<typeof setTimeout>>()
    let changeTimer: ReturnType<typeof setTimeout> | null = null

    const emitValuesChange = () => {
      if (!formRef) return
      local.onValuesChange?.(buildFormData(formRef))
    }

    const handler = (e: Event) => {
      const target = e.target as Element | null
      if (!target || !isFormElement(target)) return

      const issues = runValidation()

      if (liveMarking) {
        const el = target as FormElementType
        const prev = markTimers.get(el)
        if (prev) clearTimeout(prev)
        markTimers.set(
          el,
          setTimeout(() => syncFieldErrors(issues, el), markDebounce)
        )
      }

      if (local.onValuesChange) {
        if (changeTimer) clearTimeout(changeTimer)
        changeTimer = setTimeout(emitValuesChange, changeDebounce)
      }
    }

    formRef.addEventListener('input', handler)
    formRef.addEventListener('change', handler)
    if (modes.has('keyup')) formRef.addEventListener('keyup', handler)

    onCleanup(() => {
      if (!formRef) return
      formRef.removeEventListener('input', handler)
      formRef.removeEventListener('change', handler)
      formRef.removeEventListener('keyup', handler)
      markTimers.forEach(clearTimeout)
      if (changeTimer) clearTimeout(changeTimer)
    })
  }

  // Marca el campo como "tocado" al salir de él: hasta entonces ni el contexto
  // ni el controlador enseñan su error, para no pintar en rojo un formulario
  // recién abierto.
  const setupTouchListener = () => {
    if (!formRef) return

    const handler = (e: Event) => {
      const target = e.target as Element | null
      if (!target || !isFormElement(target)) return
      const name = (target as FormElementType).name
      if (name) {
        markTouched(name)
        local.controller?.__touch(name)
      }
    }

    formRef.addEventListener('focusout', handler)
    onCleanup(() => formRef?.removeEventListener('focusout', handler))
  }

  const bindController = () => {
    if (!local.controller) return

    local.controller.__bind({
      requestSubmit: () => formRef?.requestSubmit(),
      setFieldValue: (name, value) => {
        applyValues({ [name]: value })
        runValidation()
      },
      applyValues: values => {
        applyValues(values)
        runValidation()
      },
      // `reset()` devuelve el formulario a recién abierto: sin campos tocados,
      // sin intento de envío y sin nada marcado en rojo.
      resetFields: () => {
        setFormTouched([])
        setFormSubmitted(false)
        syncFieldErrors({})
      }
    })
  }

  onMount(() => {
    if (!formRef) return

    const initial = getInitialValues()
    if (initial) {
      applyValues(initial)
      setFormValues(initial)
    }

    bindController()
    setupTouchListener()
    setupInputListener(getValidationModes())

    setTimeout(() => {
      runValidation()
    }, 0)
  })

  // Los valores iniciales del controlador pueden ser una función: cuando el
  // recurso llega tarde se vuelven a aplicar solos, así que no hace falta
  // retrasar el pintado del formulario hasta que responda la petición.
  let lastApplied = ''
  createEffect(() => {
    const initial = local.controller?.options.initialValues
    const resolved = typeof initial === 'function' ? initial() : initial
    if (!resolved || !formRef) return

    const serialized = JSON.stringify(resolved)
    if (serialized === lastApplied) return
    lastApplied = serialized

    // Vía el controlador para que también se enteren los campos sin elemento
    // en el DOM; él acaba llamando a applyValues, que escribe y revalida.
    local.controller?.setValues(resolved as FormValuesType)
  })

  const handleSubmit = (event: SubmitEvent) => {
    event.preventDefault()
    const form = event.target as HTMLFormElement
    const validator = getValidator()

    // A partir del primer intento de envío se ven todos los errores, no sólo
    // los de los campos por los que ya se pasó.
    setFormSubmitted(true)
    local.controller?.__touchAll()

    const values = collectFormValues(form)

    if (!validator) {
      publish(values, {}, true)
      syncFieldErrors({})
      const submitData = buildFormData(form)
      local.controller?.__submit(values, submitData)
      local.onSubmit?.(submitData, values, event)
      return
    }

    // Un solo parseo. Antes se validaba el formulario entero una vez por cada
    // campo que tuviera dentro, así que enviar costaba tantos parseos del
    // esquema como campos hubiera.
    const result = validator.safeParse(values)
    const issues = result.success ? {} : collectIssues(result.error as ZodError)

    publish(values, issues, result.success)
    syncFieldErrors(issues)

    // Manda el esquema, no el DOM: un campo inválido que no tiene elemento (una
    // lista, un valor calculado) también tiene que frenar el envío, y un
    // `aria-invalid` que haya dejado ahí cualquier otro no debe frenarlo.
    if (!result.success) return

    // Lo que sale del DOM son cadenas; el esquema es quien convierte. Al
    // onSubmit se le entrega el dato ya parseado, que es lo que dice el tipo.
    const parsedValues = result.data as Record<string, unknown>
    const submitData = buildFormData(form)
    local.controller?.__submit(parsedValues, submitData)
    local.onSubmit?.(submitData, parsedValues, event)
  }

  const hasErrors = () => !formValid()

  const renderChildren = () => {
    if (typeof local.children === 'function') {
      return (local.children as (props: FormRenderPropsType) => JSX.Element)({
        valid: formValid,
        values: formValues,
        hasErrors,
        errors: contextErrors
      })
    }
    return local.children
  }

  return (
    <FormContext.Provider value={contextValue}>
      <form
        ref={el => {
          formRef = el
        }}
        {...others}
        noValidate
        onSubmit={handleSubmit}
      >
        {renderChildren()}
      </form>
    </FormContext.Provider>
  )
}

export default Form
